// frontend/src/components/MarketParamsSettings.js
// Settings sidebar for configuring NIFTY lot size
// With smart lot calculator + auto-correction

import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API_BASE } from "../config";

const LOT_UNIT = 65; // 1 NIFTY lot = 65 qty

export default function MarketParamsSettings() {
  const [params, setParams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [availableCapital, setAvailableCapital] = useState(0);
  const [saving, setSaving] = useState(null);
  const [clearing, setClearing] = useState(false);
  const [toast, setToast] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [maxCe, setMaxCe] = useState(1);
  const [maxPe, setMaxPe] = useState(1);
  const [slDistance, setSlDistance] = useState(17);
  const [targetPoints, setTargetPoints] = useState(12);
  const [itmPremium, setItmPremium] = useState("");

  const fetchParams = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_BASE}/market-params`);
      if (data?.ok) {
        setParams(data.params);
        const vals = {};
        for (const p of data.params) {
          vals[p.symbol] = p.lotSize;
        }
        setEditValues(vals);
        // Load separate CE/PE limits
        const nifty = data.params.find((p) => p.symbol === "NIFTY50");
        if (nifty) {
          setMaxCe(nifty.maxCeTrades ?? 1);
          setMaxPe(nifty.maxPeTrades ?? 1);
          setSlDistance(nifty.slDistance ?? 17);
          setTargetPoints(nifty.targetPoints ?? 12);
        }
      }
    } catch (err) {
      console.error("Failed to fetch market params:", err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Try to fetch ITM premium from market data
  useEffect(() => {
    const fetchMarketInfo = async () => {
      try {
        const [mktRes, portRes] = await Promise.all([
          axios.get(`${API_BASE}/strategy/market-data`).catch(() => ({ data: {} })),
          axios.get(`${API_BASE}/kite/portfolio`).catch(() => ({ data: {} })),
        ]);
        if (mktRes.data?.ok && mktRes.data.itm?.premium) {
          setItmPremium(mktRes.data.itm.premium);
        }
        if (portRes.data?.ok && portRes.data.equity) {
          setAvailableCapital(portRes.data.equity);
        }
      } catch (_) { }
    };
    fetchMarketInfo();
    const interval = setInterval(fetchMarketInfo, 15000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    fetchParams();
  }, [fetchParams]);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  // Auto-correct qty to nearest valid lot multiple
  const snapToLot = (val) => {
    const num = Number(val);
    if (isNaN(num) || num < 1) return LOT_UNIT;
    return Math.round(num / LOT_UNIT) * LOT_UNIT || LOT_UNIT;
  };

  const handleBlur = () => {
    const raw = editValues["NIFTY50"];
    const corrected = snapToLot(raw);
    if (Number(raw) !== corrected) {
      setEditValues((prev) => ({ ...prev, NIFTY50: corrected }));
      showToast(`Auto-corrected to ${corrected} (${corrected / LOT_UNIT} lots × ${LOT_UNIT})`, "info");
    }
  };

  const handleSave = async (symbol) => {
    const corrected = snapToLot(editValues[symbol]);
    setEditValues((prev) => ({ ...prev, [symbol]: corrected }));

    setSaving(symbol);
    try {
      const { data } = await axios.put(`${API_BASE}/market-params/${symbol}`, {
        lotSize: corrected,
        maxCeTrades: Number(maxCe) || 1,
        maxPeTrades: Number(maxPe) || 1,
      });
      if (data?.ok) {
        showToast(`Saved: ${corrected} qty (${corrected / LOT_UNIT} lots)`);
        fetchParams();
      }
    } catch (err) {
      showToast(`Failed: ${err.message}`, "error");
    } finally {
      setSaving(null);
    }
  };

  const handleClearLimits = async () => {
    if (!window.confirm("Are you sure you want to clear all trade limits? This will reset the position locks for all symbols.")) return;

    setClearing(true);
    try {
      const { data } = await axios.post(`${API_BASE}/market-params/clear-limits`);
      if (data?.ok) {
        showToast("Trade limits cleared!");
        fetchParams();
      }
    } catch (err) {
      showToast(`Failed: ${err.message}`, "error");
    } finally {
      setClearing(false);
    }
  };

  if (loading) {
    return (
      <div className="mps-loading">
        <span className="spinner-sm"></span> Loading...
      </div>
    );
  }

  const niftyParam = params.find((p) => p.symbol === "NIFTY50");
  const currentQty = Number(editValues["NIFTY50"] ?? niftyParam?.lotSize ?? 65);
  const lots = Math.round(currentQty / LOT_UNIT);
  const premium = Number(itmPremium) || 0;
  const capitalRequired = premium > 0 ? currentQty * premium : 0;

  // Suggested max qty based on available capital
  const suggestedMaxQty = premium > 0 && availableCapital > 0
    ? Math.floor(availableCapital / premium / LOT_UNIT) * LOT_UNIT
    : 0;
  const suggestedLots = suggestedMaxQty / LOT_UNIT;

  return (
    <div className="mps-root">
      {toast && (
        <div className={`mps-toast ${toast.type}`}>
          {toast.type === "success" ? "✅" : toast.type === "error" ? "❌" : "ℹ️"} {toast.msg}
        </div>
      )}

      {/* Lot Size Input */}
      <div className="mps-section">
        <label className="mps-label">Lot Size (Quantity)</label>
        <div className="mps-input-group">
          <input
            id="nifty-lot-size-input"
            type="number"
            min="1"
            step={LOT_UNIT}
            className="mps-input"
            value={editValues["NIFTY50"] ?? niftyParam?.lotSize ?? ""}
            onChange={(e) =>
              setEditValues((prev) => ({ ...prev, NIFTY50: e.target.value }))
            }
            onBlur={handleBlur}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSave("NIFTY50");
            }}
          />
          <button
            id="nifty-lot-size-save-btn"
            className={`mps-save-btn ${saving === "NIFTY50" ? "saving" : ""}`}
            onClick={() => handleSave("NIFTY50")}
            disabled={saving === "NIFTY50"}
          >
            {saving === "NIFTY50" ? "Saving..." : "💾 Save"}
          </button>
        </div>

        {/* Lot info tag */}
        <div className="mps-lot-info">
          <span className="mps-tag">{lots} lot{lots !== 1 ? "s" : ""}</span>
          <span className="mps-tag-dim">× {LOT_UNIT} qty/lot</span>
        </div>
      </div>

      {/* Per-Side Trade Limits (CE / PE) — auto-save */}
      <div className="mps-section">
        <label className="mps-label">Max Trades Per Day</label>
        <div className="mps-side-row">
          <div className="mps-side-input">
            <span className="mps-side-label ce">CE</span>
            <input
              type="number" min="1" max="10" className="mps-input mps-input-sm"
              value={maxCe}
              onChange={(e) => setMaxCe(e.target.value)}
              onBlur={() => {
                const v = Math.max(1, Number(maxCe) || 1);
                setMaxCe(v);
                axios.patch(`${API_BASE}/market-params/NIFTY50/trade-limits`, { maxCeTrades: v }).catch(() => { });
                showToast(`CE limit saved: ${v}`, "info");
              }}
            />
          </div>
          <div className="mps-side-input">
            <span className="mps-side-label pe">PE</span>
            <input
              type="number" min="1" max="10" className="mps-input mps-input-sm"
              value={maxPe}
              onChange={(e) => setMaxPe(e.target.value)}
              onBlur={() => {
                const v = Math.max(1, Number(maxPe) || 1);
                setMaxPe(v);
                axios.patch(`${API_BASE}/market-params/NIFTY50/trade-limits`, { maxPeTrades: v }).catch(() => { });
                showToast(`PE limit saved: ${v}`, "info");
              }}
            />
          </div>
        </div>
        <p className="mps-hint">
          Max CE and PE trades allowed per day. Auto-saves on change.
        </p>
      </div>

      {/* Strategy Parameters (SL / TGT) — auto-save */}
      <div className="mps-section">
        <label className="mps-label">Strategy Parameters</label>
        <div className="mps-side-row">
          <div className="mps-side-input">
            <span className="mps-side-label sl">SL</span>
            <input
              type="number" min="0" max="100" className="mps-input mps-input-sm"
              value={slDistance}
              onChange={(e) => setSlDistance(e.target.value)}
              onBlur={() => {
                const v = Math.max(0, Number(slDistance) || 0);
                setSlDistance(v);
                axios.patch(`${API_BASE}/market-params/NIFTY50/trade-limits`, { slDistance: v }).catch(() => { });
                showToast(`SL saved: ${v}`, "info");
              }}
            />
          </div>
          <div className="mps-side-input">
            <span className="mps-side-label tgt">TGT</span>
            <input
              type="number" min="0" max="100" className="mps-input mps-input-sm"
              value={targetPoints}
              onChange={(e) => setTargetPoints(e.target.value)}
              onBlur={() => {
                const v = Math.max(0, Number(targetPoints) || 0);
                setTargetPoints(v);
                axios.patch(`${API_BASE}/market-params/NIFTY50/trade-limits`, { targetPoints: v }).catch(() => { });
                showToast(`TGT saved: ${v}`, "info");
              }}
            />
          </div>
        </div>
        <p className="mps-hint">
          Stop Loss and Target points for NIFTY. Auto-saves on change. Set Target to 0 for trailing-only.
        </p>
      </div>

      {/* Capital Calculator */}
      <div className="mps-section mps-calc">
        <label className="mps-label">📊 Capital Calculator</label>
        <div className="mps-calc-grid">
          <div className="mps-calc-row">
            <span className="mps-calc-label">ITM Premium</span>
            <span className="mps-calc-value">
              {premium > 0 ? `₹${premium.toFixed(2)}` : "—"}
            </span>
          </div>
          <div className="mps-calc-row">
            <span className="mps-calc-label">Quantity</span>
            <span className="mps-calc-value">{currentQty}</span>
          </div>
          <div className="mps-calc-divider" />
          <div className="mps-calc-row total">
            <span className="mps-calc-label">Capital Required</span>
            <span className={`mps-calc-value ${capitalRequired > availableCapital && availableCapital > 0 ? "over-budget" : "highlight"}`}>
              {capitalRequired > 0
                ? `₹${capitalRequired.toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
                : "Waiting for ITM data..."}
            </span>
          </div>

          {/* Available Capital */}
          <div className="mps-calc-row">
            <span className="mps-calc-label">Available Capital</span>
            <span className="mps-calc-value">
              {availableCapital > 0
                ? `₹${availableCapital.toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
                : "—"}
            </span>
          </div>

          {/* Suggested Max Qty */}
          {suggestedMaxQty > 0 && (
            <>
              <div className="mps-calc-divider" />
              <div className="mps-calc-row suggest">
                <span className="mps-calc-label">
                  Suggested Max
                </span>
                <span className="mps-calc-value suggest-value">
                  {suggestedMaxQty} qty ({suggestedLots} lots)
                </span>
              </div>
              <button
                className="mps-apply-btn"
                onClick={() => {
                  setEditValues((prev) => ({ ...prev, NIFTY50: suggestedMaxQty }));
                  showToast(`Set to ${suggestedMaxQty} (${suggestedLots} lots) based on your capital`, "info");
                }}
              >
                ⚡ Apply Suggested Qty
              </button>
            </>
          )}

          {/* Over budget warning */}
          {capitalRequired > availableCapital && availableCapital > 0 && capitalRequired > 0 && (
            <div className="mps-warning">
              ⚠️ Current qty exceeds your capital by ₹{(capitalRequired - availableCapital).toLocaleString("en-IN")}
            </div>
          )}
        </div>
        <p className="mps-hint">
          Auto-fetches ITM premium from live market data. Capital = Premium × Qty.
        </p>
      </div>

      <p className="mps-hint" style={{ marginTop: 16 }}>
        1 lot = {LOT_UNIT} qty. If you enter an invalid quantity (e.g. 275), it auto-corrects
        to the nearest lot multiple ({LOT_UNIT * 4} = 4 lots).
      </p>

      <div className="mps-section mps-danger-section">
        <button
          className="mps-btn-danger"
          onClick={handleClearLimits}
          disabled={clearing}
        >
          {clearing ? <span className="spinner-sm"></span> : "🔓"} Clear All Trade Limits
        </button>
        <p className="mps-hint">
          Use this if you manually exit a trade on Zerodha and the algo is still blocking new entries.
        </p>
      </div>

      <style>{`
        .mps-root {
          padding: 4px 0;
        }

        .mps-loading {
          padding: 30px;
          text-align: center;
          color: var(--color-text-secondary);
        }

        .mps-toast {
          padding: 10px 14px;
          border-radius: var(--radius-md);
          margin-bottom: 16px;
          font-weight: 500;
          font-size: 0.82rem;
          animation: mpsSlideIn 0.3s ease;
        }
        .mps-toast.success {
          background: rgba(34, 197, 94, 0.15);
          border: 1px solid rgba(34, 197, 94, 0.3);
          color: #22c55e;
        }
        .mps-toast.error {
          background: rgba(239, 68, 68, 0.15);
          border: 1px solid rgba(239, 68, 68, 0.3);
          color: #ef4444;
        }
        .mps-toast.info {
          background: rgba(99, 102, 241, 0.15);
          border: 1px solid rgba(99, 102, 241, 0.3);
          color: #818cf8;
        }
        @keyframes mpsSlideIn {
          from { opacity: 0; transform: translateY(-8px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .mps-section {
          margin-bottom: 20px;
        }

        .mps-label {
          display: block;
          font-size: 0.78rem;
          font-weight: 600;
          color: var(--color-text-secondary);
          margin-bottom: 8px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .mps-input-group {
          display: flex;
          gap: 10px;
          align-items: stretch;
        }

        .mps-input {
          flex: 1;
          min-width: 0;
          padding: 10px 14px;
          font-size: 1.05rem;
          font-family: var(--font-family-mono);
          font-weight: 600;
          background: rgba(0, 0, 0, 0.3);
          border: 1px solid var(--color-border);
          border-radius: var(--radius-md);
          color: var(--color-text-primary);
          outline: none;
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .mps-input:focus {
          border-color: var(--color-accent, #6366f1);
          box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
        }
        .mps-input::-webkit-inner-spin-button,
        .mps-input::-webkit-outer-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }

        .mps-save-btn {
          padding: 10px 18px;
          font-size: 0.82rem;
          font-weight: 600;
          background: linear-gradient(135deg, #6366f1, #8b5cf6);
          color: white;
          border: none;
          border-radius: var(--radius-md);
          cursor: pointer;
          transition: all 0.2s;
          white-space: nowrap;
          flex-shrink: 0;
        }
        .mps-save-btn:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(99, 102, 241, 0.4);
        }
        .mps-save-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .mps-lot-info {
          margin-top: 8px;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .mps-tag {
          background: rgba(99, 102, 241, 0.15);
          color: #818cf8;
          padding: 3px 10px;
          border-radius: var(--radius-full);
          font-size: 0.75rem;
          font-weight: 600;
          font-family: var(--font-family-mono);
        }

        .mps-tag-dim {
          color: var(--color-text-muted);
          font-size: 0.72rem;
        }

        /* Side-by-side CE/PE inputs */
        .mps-side-row {
          display: flex;
          gap: 12px;
          align-items: center;
        }

        .mps-side-input {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .mps-side-label {
          font-size: 0.78rem;
          font-weight: 700;
          padding: 4px 10px;
          border-radius: var(--radius-sm);
          font-family: var(--font-family-mono);
        }

        .mps-side-label.ce {
          background: rgba(34, 197, 94, 0.15);
          color: #22c55e;
        }

        .mps-side-label.pe {
          background: rgba(239, 68, 68, 0.15);
          color: #ef4444;
        }

        .mps-side-label.sl {
          background: rgba(34, 197, 94, 0.15);
          color: #22c55e;
        }

        .mps-side-label.tgt {
          background: rgba(245, 158, 11, 0.15);
          color: #f59e0b;
        }

        .mps-input-sm {
          width: 60px;
          padding: 8px 10px;
          font-size: 0.95rem;
          text-align: center;
        }

        /* Calculator */
        .mps-calc {
          background: rgba(0, 0, 0, 0.2);
          border: 1px solid var(--color-border);
          border-radius: var(--radius-md);
          padding: 16px;
        }

        .mps-calc .mps-label {
          margin-bottom: 12px;
        }

        .mps-calc-grid {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .mps-calc-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .mps-calc-label {
          color: var(--color-text-muted);
          font-size: 0.82rem;
        }

        .mps-calc-value {
          font-family: var(--font-family-mono);
          font-weight: 600;
          color: var(--color-text-secondary);
          font-size: 0.9rem;
        }

        .mps-calc-divider {
          height: 1px;
          background: var(--color-border);
          margin: 4px 0;
        }

        .mps-calc-row.total .mps-calc-label {
          font-weight: 600;
          color: var(--color-text-primary);
        }

        .mps-calc-value.highlight {
          color: #22c55e;
          font-size: 1rem;
        }

        .mps-calc-value.over-budget {
          color: #ef4444;
          font-size: 1rem;
        }

        .mps-calc-row.suggest .mps-calc-label {
          color: #818cf8;
          font-weight: 600;
        }

        .suggest-value {
          color: #818cf8 !important;
        }

        .mps-apply-btn {
          margin-top: 10px;
          width: 100%;
          padding: 9px 16px;
          font-size: 0.82rem;
          font-weight: 600;
          background: rgba(99, 102, 241, 0.15);
          border: 1px solid rgba(99, 102, 241, 0.3);
          border-radius: var(--radius-md);
          color: #818cf8;
          cursor: pointer;
          transition: all 0.2s;
        }

        .mps-apply-btn:hover {
          background: rgba(99, 102, 241, 0.25);
          border-color: rgba(99, 102, 241, 0.5);
        }

        .mps-warning {
          margin-top: 10px;
          padding: 8px 12px;
          background: rgba(239, 68, 68, 0.1);
          border: 1px solid rgba(239, 68, 68, 0.25);
          border-radius: var(--radius-sm);
          color: #ef4444;
          font-size: 0.75rem;
          font-weight: 500;
        }

        .mps-hint {
          margin: 8px 0 0 0;
          font-size: 0.72rem;
          color: var(--color-text-muted);
          line-height: 1.5;
        }

        .mps-danger-section {
          margin-top: 32px;
          padding-top: 24px;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
        }

        .mps-btn-danger {
          width: 100%;
          padding: 12px;
          background: rgba(239, 68, 68, 0.1);
          border: 1px solid rgba(239, 68, 68, 0.2);
          border-radius: 8px;
          color: #f87171;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }

        .mps-btn-danger:hover:not(:disabled) {
          background: rgba(239, 68, 68, 0.2);
          border-color: rgba(239, 68, 68, 0.4);
          color: #ef4444;
        }

        .mps-btn-danger:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .spinner-sm {
          display: inline-block;
          width: 14px;
          height: 14px;
          border: 2px solid rgba(255,255,255,0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 0.6s linear infinite;
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
